"use client";

import { Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { getWhitelabelVisibility } from "@/hooks/useWhitelabelVisibility";
import type { SocialLink, SupportedSocialPlatform } from "@/lib/whitelabel-config";

/**
 * Social media icon components for each supported platform
 */
const SocialIcons: Record<SupportedSocialPlatform, React.FC<{ className?: string }>> = {
  twitter: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
    </svg>
  ),
  linkedin: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
    </svg>
  ),
  instagram: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  ),
  facebook: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  ),
  youtube: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
  tiktok: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  ),
  discord: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  ),
  telegram: ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012.056 0h-.112zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  ),
};

/**
 * Get the display name for a social platform
 */
function getPlatformLabel(platform: SupportedSocialPlatform): string {
  const labels: Record<SupportedSocialPlatform, string> = {
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    discord: 'Discord',
    telegram: 'Telegram',
  };
  return labels[platform];
}

/**
 * Props for the Footer component
 */
export interface FooterProps {
  /** Tenant configuration for whitelabel mode */
  tenant?: {
    name?: string;
    slug?: string;
    footerLogoUrl?: string | null;
    footerTransparentLogoUrl?: string | null;
    socialLinks?: SocialLink[] | null;
    footerBackgroundColor?: string | null;
    footerTextColor?: string | null;
    footerLinkColor?: string | null;
    footerTransparentOnHomepage?: boolean;
    footerTransparentTextColor?: string | null;
    footerTransparentLinkColor?: string | null;
    textColor?: string | null;
    primaryColor?: string | null;
    /** Custom footer text shown after "All rights reserved." (whitelabel) */
    footerCustomText?: string | null;
    /** Privacy Policy / Terms of Service footer link config (whitelabel) */
    privacyPolicyEnabled?: boolean;
    privacyPolicyType?: string | null;
    privacyPolicyUrl?: string | null;
    termsOfServiceEnabled?: boolean;
    termsOfServiceType?: string | null;
    termsOfServiceUrl?: string | null;
  } | null;
  /** User role for visibility control */
  userRole?: string | null;
  /** Whether we're on the homepage (for transparent footer option) */
  isHomepage?: boolean;
}

/**
 * Footer component with whitelabel support
 *
 * In SaaS mode (whitelabel disabled):
 * - Shows standard SaaS branding and links
 * - Displays Status, API Docs, Changelog, Pricing, Resource Center, Support
 *
 * In whitelabel mode:
 * - Hides SaaS-specific links
 * - Shows tenant's social links if configured
 * - Shows tenant's footer logo if available
 * - Uses tenant's footer background color
 */
export function Footer({ tenant, userRole, isHomepage: isHomepageProp }: FooterProps) {
  const pathname = usePathname();
  const visibility = getWhitelabelVisibility(userRole);
  const { showSaasFooterLinks, showBrowseHunts, showHowItWorks, isWhitelabelMode } = visibility;

  // Get visible social links from tenant config
  const visibleSocialLinks = (tenant?.socialLinks || []).filter(
    (link): link is SocialLink => link.visible && !!link.url
  );

  // Detect if we're on the homepage - either from prop or pathname
  // Only the root "/" is considered homepage for transparent footer (not tenant pages like /inspire)
  const isHomepage = isHomepageProp ?? (pathname === '/');

  // Check if we should use transparent background on homepage
  const useTransparentBg = isHomepage && tenant?.footerTransparentOnHomepage;

  // Determine footer colors - use transparent-specific colors when in transparent mode
  const footerBgColor = tenant?.footerBackgroundColor || '#F9FAFB'; // Light gray default for whitelabel
  const footerTextColor = useTransparentBg
    ? (tenant?.footerTransparentTextColor || '#FFFFFF')
    : (tenant?.footerTextColor || '#9CA3AF');
  const footerLinkColor = useTransparentBg
    ? (tenant?.footerTransparentLinkColor || '#FFFFFF')
    : (tenant?.footerLinkColor || '#3B82F6');

  // Determine logo based on transparent state
  // Use transparent logo when in transparent mode, fall back to default footer logo
  const footerLogoUrl = useTransparentBg && tenant?.footerTransparentLogoUrl
    ? tenant.footerTransparentLogoUrl
    : tenant?.footerLogoUrl;

  // In whitelabel mode with tenant config
  if (isWhitelabelMode && tenant) {
    return (
      <footer
        style={{
          backgroundColor: useTransparentBg ? 'transparent' : footerBgColor,
          color: footerTextColor,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Organization Info with Social Links */}
          <div className="flex flex-col items-center text-center mb-6">
            {footerLogoUrl ? (
              <Image
                src={footerLogoUrl}
                alt={tenant.name || 'Organization'}
                width={180}
                height={48}
                className="h-12 w-auto mb-4"
                unoptimized
              />
            ) : (
              <h3
                className="text-lg font-semibold mb-4"
                style={{ color: footerTextColor }}
              >
                {tenant.name || 'Organization'}
              </h3>
            )}
            {visibleSocialLinks.length > 0 && (
              <div className="flex space-x-4">
                {visibleSocialLinks.map((link) => {
                  const IconComponent = SocialIcons[link.platform];
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-80 transition-opacity"
                      style={{ color: footerLinkColor }}
                      aria-label={getPlatformLabel(link.platform)}
                    >
                      <IconComponent className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Bar - Copyright and Legal Links */}
          <div
            className="border-t pt-6 flex flex-col md:flex-row justify-center items-center gap-2 md:gap-6"
            style={{ borderColor: `${footerTextColor}20` }}
          >
            <p className="text-sm" style={{ color: footerTextColor }}>
              &copy; {new Date().getFullYear()} {tenant.name || 'Organization'}. All rights reserved.
              {tenant.footerCustomText ? ` ${tenant.footerCustomText}` : ''}
            </p>
            {(() => {
              // Build the enabled legal links. EXTERNAL links open in a new tab;
              // CUSTOM links point to the hosted /privacy and /terms pages.
              const legalLinks: { label: string; href: string; external: boolean }[] = [];
              if (tenant.privacyPolicyEnabled !== false) {
                const external = tenant.privacyPolicyType === 'EXTERNAL' && !!tenant.privacyPolicyUrl;
                legalLinks.push({ label: 'Privacy Policy', href: external ? tenant.privacyPolicyUrl! : '/privacy', external });
              }
              if (tenant.termsOfServiceEnabled !== false) {
                const external = tenant.termsOfServiceType === 'EXTERNAL' && !!tenant.termsOfServiceUrl;
                legalLinks.push({ label: 'Terms of Service', href: external ? tenant.termsOfServiceUrl! : '/terms', external });
              }
              if (legalLinks.length === 0) return null;
              return (
                <div className="flex items-center gap-4">
                  {legalLinks.map((l, i) => (
                    <Fragment key={l.label}>
                      {i > 0 && <span style={{ color: footerTextColor, opacity: 0.4 }}>|</span>}
                      {l.external ? (
                        <a
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm hover:opacity-80 transition-opacity"
                          style={{ color: footerLinkColor }}
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          href={l.href}
                          className="text-sm hover:opacity-80 transition-opacity"
                          style={{ color: footerLinkColor }}
                        >
                          {l.label}
                        </Link>
                      )}
                    </Fragment>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </footer>
    );
  }

  // Standard SaaS Footer
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">DA SaaS</h3>
            <p className="text-gray-300 mb-4">
              Create engaging scavenger hunts with digital rewards. Perfect for events,
              marketing campaigns, and community engagement.
            </p>
            <div className="flex space-x-4">
              <a
                href="#"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <SocialIcons.twitter className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <SocialIcons.linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {showBrowseHunts && (
                <li>
                  <Link href="/browse" className="text-gray-300 hover:text-white transition-colors">
                    Browse Experiences
                  </Link>
                </li>
              )}
              {showHowItWorks && (
                <li>
                  <Link href="/about" className="text-gray-300 hover:text-white transition-colors">
                    How It Works
                  </Link>
                </li>
              )}
              {showSaasFooterLinks && (
                <li>
                  <Link href="/pricing" className="text-gray-300 hover:text-white transition-colors">
                    Pricing
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Support</h3>
            <ul className="space-y-2">
              {showSaasFooterLinks && (
                <li>
                  <Link href="/help" className="text-gray-300 hover:text-white transition-colors">
                    Resource Center
                  </Link>
                </li>
              )}
              <li>
                <Link href="/contact" className="text-gray-300 hover:text-white transition-colors">
                  Contact Us
                </Link>
              </li>
              {showSaasFooterLinks && (
                <li>
                  <Link href="/security" className="text-gray-300 hover:text-white transition-colors">
                    Support
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} DA SaaS. All rights reserved. Powered by{' '}
              <a
                href="https://detroitledger.tech"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Detroit Ledger Technologies
              </a>
            </p>
            <div className="flex items-center gap-4">
              <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
          {showSaasFooterLinks && (
            <div className="flex space-x-6">
              <Link href="/changelog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Changelog
              </Link>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
